import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { mdiTrashCan } from '@mdi/js';

import { DeleteLocationsDocument } from 'graphql/types';
import { BoxLoader } from 'common/components/Loader';
import {
    TableActionBar,
    TableCell,
    TableHead,
    useSelection,
} from 'common/components/Table';
import { Checkbox } from 'common/components/Checkbox';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { FilterBar } from 'common/components/FilterBar';
import { IconButton } from 'common/components/IconButton';
import { Pagination } from 'common/components/Pagination';
import { Tooltip } from 'common/components/Tooltip';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useLocationList } from 'organisation/hooks/useLocationList';
import { evictMeetingModulesByLocationIds } from 'training/utils/module';

import { LocationsManagementTableRow } from './LocationsManagementTableRow';

export const LocationsManagementTable = () => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const {
        locations,
        loading,
        paginationSettings,
        filterValues,
        setPage,
        onSearch,
    } = useLocationList();

    const [deleteLocations, { loading: deleteLoading }] = useMutation(
        DeleteLocationsDocument
    );

    const { ...selectionProps } = useSelection(locations);
    const { isSelected, isSelectedAll, selected, setSelected, onSelectAll } =
        selectionProps;

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch(searchValue);
    };

    const handleDelete = async () => {
        let failedIds: string[] = [];

        try {
            const response = await deleteLocations({
                variables: { ids: selected },
                update: (cache, result) => {
                    const succeededIds =
                        result?.data?.deleteLocations?.bulkDelete
                            ?.succeededIds || [];
                    if (!succeededIds.length) return;

                    cache.evict({ fieldName: 'locations' });
                    cache.evict({ fieldName: 'paginatedLocations' });
                    evictMeetingModulesByLocationIds(cache, succeededIds);
                    cache.gc();
                },
            });

            const { bulkDelete } = response.data?.deleteLocations || {};
            failedIds = bulkDelete?.failedIds || [];
            const succeededIds = bulkDelete?.succeededIds || [];

            if (failedIds.length) {
                displaySnackbar(
                    translate(
                        'locationsManagementPage.actionSuccess.bulkDeleteFailed',
                        { count: failedIds.length }
                    ),
                    { variant: 'error' }
                );
            }

            if (succeededIds.length) {
                displaySnackbar(
                    translate(
                        'locationsManagementPage.actionSuccess.bulkDelete',
                        { count: succeededIds.length }
                    ),
                    { variant: 'success' }
                );
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!failedIds.length) {
            setShowDeleteDialog(false);
            setSelected([]);
        } else {
            // Keep the dialog open and narrow the selection to the failed ids
            // so confirming again retries only those (SCORM-style retry flow).
            setSelected(failedIds);
        }
    };

    const tableActions = (
        <Tooltip title={translate('locationsManagementPage.bulkDeleteTooltip')}>
            <Box>
                <IconButton
                    disabled={!isSelected}
                    iconPath={mdiTrashCan}
                    iconSize="2.4rem"
                    onClick={() => setShowDeleteDialog(true)}
                />
            </Box>
        </Tooltip>
    );

    return (
        <>
            <FilterBar
                disabled={loading}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {loading ? (
                <BoxLoader />
            ) : (
                <>
                    <Box sx={{ mt: 2 }}>
                        {!locations.length ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 7,
                                }}
                            >
                                <Typography>
                                    {translate(
                                        'locationsManagementPage.noResults'
                                    )}
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <TableActionBar
                                    actions={tableActions}
                                    selectionProps={selectionProps}
                                />
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isSelectedAll}
                                                    onChange={onSelectAll}
                                                />
                                            </TableCell>
                                            <TableCell colSpan={2}>
                                                {translate('name')}
                                            </TableCell>
                                            <TableCell>
                                                {translate(
                                                    'locationsManagementPage.address'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectionProps.rows.map(
                                            ({
                                                index,
                                                isSelected,
                                                onSelect,
                                            }) => {
                                                const location =
                                                    locations[index];

                                                if (!location) return null;

                                                return (
                                                    <LocationsManagementTableRow
                                                        isSelected={isSelected}
                                                        key={location.id}
                                                        location={location}
                                                        onSelect={onSelect}
                                                    />
                                                );
                                            }
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    <Box sx={{ mt: 4 }}>
                        <Pagination
                            page={paginationSettings.page}
                            pageAmount={paginationSettings.pageAmount}
                            totalsAmount={paginationSettings.count}
                            totalsText={translate('location', {
                                count: !paginationSettings.count
                                    ? 0
                                    : paginationSettings.count,
                            })}
                            onChange={setPage}
                        />
                    </Box>

                    <ConfirmDialog
                        confirmText={translate('delete')}
                        loading={deleteLoading}
                        open={showDeleteDialog}
                        title={translate(
                            'locationsManagementPage.locationsDeleteDialog.title',
                            { count: selected.length }
                        )}
                        onCancel={() => setShowDeleteDialog(false)}
                        onClose={() => setShowDeleteDialog(false)}
                        onConfirm={handleDelete}
                    >
                        {translate(
                            'locationsManagementPage.locationsDeleteDialog.text',
                            { count: selected.length }
                        )}
                    </ConfirmDialog>
                </>
            )}
        </>
    );
};
